const normalizeVariants = (variants = []) =>
  variants.map((variant) => ({
    weight: Number(variant.weight) || 400,
    style: variant.style === "italic" ? "italic" : "normal",
  }));

export const normalizeFontDescriptor = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    return { family: value };
  }
  if (typeof value === "object") {
    const descriptor = {};
    if (value.family) {
      descriptor.family = value.family;
    }
    if (value.weight !== undefined) {
      const normalizedWeight = Number(value.weight);
      if (!Number.isNaN(normalizedWeight)) {
        descriptor.weight = normalizedWeight;
      }
    }
    if (value.style) {
      descriptor.style = value.style === "italic" ? "italic" : "normal";
    }
    if (Object.keys(descriptor).length === 0) {
      return null;
    }
    return descriptor;
  }
  return null;
};

export const GOOGLE_FONT_OPTIONS = [
  {
    family: "Playfair Display",
    label: "Playfair Display",
    variants: normalizeVariants([
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 700 },
      { weight: 400, style: "italic" },
      { weight: 600, style: "italic" },
    ]),
  },
  {
    family: "Pinyon Script",
    label: "Pinyon Script",
    variants: normalizeVariants([{ weight: 400 }]),
  },
  {
    family: "Lato",
    label: "Lato",
    variants: normalizeVariants([
      { weight: 300 },
      { weight: 400 },
      { weight: 700 },
      { weight: 300, style: "italic" },
      { weight: 400, style: "italic" },
      { weight: 700, style: "italic" },
    ]),
  },
  {
    family: "Montserrat",
    label: "Montserrat",
    variants: normalizeVariants([
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 700 },
    ]),
  },
  {
    family: "Libre Baskerville",
    label: "Libre Baskerville",
    variants: normalizeVariants([
      { weight: 400 },
      { weight: 700 },
      { weight: 400, style: "italic" },
    ]),
  },
  {
    family: "Source Sans Pro",
    label: "Source Sans Pro",
    variants: normalizeVariants([
      { weight: 300 },
      { weight: 400 },
      { weight: 600 },
      { weight: 700 },
      { weight: 300, style: "italic" },
      { weight: 400, style: "italic" },
      { weight: 600, style: "italic" },
      { weight: 700, style: "italic" },
    ]),
  },
  {
    family: "Raleway",
    label: "Raleway",
    variants: normalizeVariants([
      { weight: 300 },
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 700 },
      { weight: 400, style: "italic" },
      { weight: 600, style: "italic" },
    ]),
  },
  {
    family: "Josefin Sans",
    label: "Josefin Sans",
    variants: normalizeVariants([
      { weight: 300 },
      { weight: 400 },
      { weight: 600 },
      { weight: 300, style: "italic" },
      { weight: 400, style: "italic" },
      { weight: 600, style: "italic" },
    ]),
  },
  {
    family: "Baskervville",
    label: "Baskervville (Baskerville-style)",
    variants: normalizeVariants([
      { weight: 400 },
      { weight: 400, style: "italic" },
    ]),
  },
  {
    family: "Open Sans",
    label: "Open Sans",
    variants: normalizeVariants([
      { weight: 300 },
      { weight: 400 },
      { weight: 600 },
      { weight: 700 },
      { weight: 300, style: "italic" },
      { weight: 400, style: "italic" },
      { weight: 600, style: "italic" },
      { weight: 700, style: "italic" },
    ]),
  },
  {
    family: "Spectral",
    label: "Spectral",
    variants: normalizeVariants([
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 400, style: "italic" },
      { weight: 500, style: "italic" },
      { weight: 600, style: "italic" },
    ]),
  },
  {
    family: "Lora",
    label: "Lora",
    variants: normalizeVariants([
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 700 },
      { weight: 400, style: "italic" },
      { weight: 500, style: "italic" },
      { weight: 600, style: "italic" },
      { weight: 700, style: "italic" },
    ]),
  },
  {
    family: "Ruslan Display",
    label: "Ruslan Display",
    variants: normalizeVariants([{ weight: 400 }]),
  },
  {
    family: "Marck Script",
    label: "Marck Script",
    variants: normalizeVariants([{ weight: 400 }]),
  },
  {
    family: "Cormorant Infant",
    label: "Cormorant Infant",
    variants: normalizeVariants([
      { weight: 300 },
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 700 },
    ]),
  },
  {
    family: "Lobster",
    label: "Lobster",
    variants: normalizeVariants([{ weight: 400 }]),
  },
  {
    family: "Playpen Sans",
    label: "Playpen Sans",
    variants: normalizeVariants([
      { weight: 100 },
      { weight: 200 },
      { weight: 300 },
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 700 },
      { weight: 800 },
    ]),
  },
  {
    family: "Montserrat Alternates",
    label: "Montserrat Alternates",
    variants: normalizeVariants([
      { weight: 400 },
      { weight: 500 },
      { weight: 600 },
      { weight: 700 },
      { weight: 800 },
    ]),
  },
  {
    family: "Poiret One",
    label: "Poiret One",
    variants: normalizeVariants([{ weight: 400 }]),
  },
];

export const FONT_SETTING_KEYS = ["title", "subtitle", "body"];

export const DEFAULT_FONT_SETTINGS = {
  body: { family: "Montserrat", weight: 400, style: "normal" },
};

export const findGoogleFont = (family) =>
  GOOGLE_FONT_OPTIONS.find((font) => font.family === family) || null;

export const sanitizeFontSettings = (fonts = {}) => {
  if (!fonts || typeof fonts !== "object") {
    return {};
  }
  const sanitized = Object.entries(fonts).reduce((acc, [key, value]) => {
    const normalized = normalizeFontDescriptor(value);
    if (normalized) {
      acc[key] = normalized;
    }
    return acc;
  }, {});
  return Object.keys(sanitized).length ? sanitized : {};
};

export const ensureValidFontVariant = (family, weight, style) => {
  const font = findGoogleFont(family);
  if (!font) {
    return null;
  }
  const normalizedWeight = Number(weight) || font.variants[0]?.weight || 400;
  const normalizedStyle = style === "italic" ? "italic" : "normal";
  const exactMatch =
    font.variants.find(
      (variant) =>
        variant.weight === normalizedWeight && variant.style === normalizedStyle
    ) || null;
  if (exactMatch) {
    return { family: font.family, weight: exactMatch.weight, style: exactMatch.style };
  }
  const anyWeightMatch =
    font.variants.find((variant) => variant.weight === normalizedWeight) ||
    font.variants[0] ||
    null;
  if (!anyWeightMatch) {
    return null;
  }
  return { family: font.family, weight: anyWeightMatch.weight, style: anyWeightMatch.style };
};

export const getAvailableFontStyles = (family) => {
  const font = findGoogleFont(family);
  if (!font) {
    return [];
  }
  const styles = new Set(font.variants.map((variant) => variant.style));
  return Array.from(styles);
};

export const getAvailableFontWeights = (family, style) => {
  const font = findGoogleFont(family);
  if (!font) {
    return [];
  }
  const filtered = font.variants.filter((variant) => {
    if (style === "italic") {
      return variant.style === "italic";
    }
    if (style === "normal") {
      return variant.style === "normal";
    }
    return true;
  });
  const weights = new Set(filtered.map((variant) => variant.weight));
  return Array.from(weights).sort((a, b) => a - b);
};

export const isGoogleFontFamily = (family) => Boolean(findGoogleFont(family));

const encodeFamily = (family) => family.replace(/\s+/g, "+");

export const buildGoogleFontsUrl = (descriptors = []) => {
  if (!descriptors.length) {
    return "";
  }

  const familyVariants = new Map();
  descriptors.forEach((descriptor) => {
    if (!descriptor?.family) {
      return;
    }
    const normalized = ensureValidFontVariant(
      descriptor.family,
      descriptor.weight,
      descriptor.style
    );
    if (!normalized) {
      return;
    }
    const entry =
      familyVariants.get(normalized.family) || {
        normal: new Set(),
        italic: new Set(),
      };
    if (normalized.style === "italic") {
      entry.italic.add(normalized.weight || 400);
    } else {
      entry.normal.add(normalized.weight || 400);
    }
    familyVariants.set(normalized.family, entry);
  });

  if (!familyVariants.size) {
    return "";
  }

  const familyParams = Array.from(familyVariants.entries()).map(
    ([family, { normal, italic }]) => {
      const rows = [];
      Array.from(normal)
        .sort((a, b) => a - b)
        .forEach((weight) => rows.push(`0,${weight}`));
      Array.from(italic)
        .sort((a, b) => a - b)
        .forEach((weight) => rows.push(`1,${weight}`));
      if (!rows.length) {
        return `family=${encodeFamily(family)}`;
      }
      return `family=${encodeFamily(family)}:ital,wght@${rows.join(";")}`;
    }
  );

  return `https://fonts.googleapis.com/css2?${familyParams.join("&")}&display=swap`;
};

export const GOOGLE_FONT_FAMILY_OPTIONS = GOOGLE_FONT_OPTIONS.map((font) => ({
  value: font.family,
  label: font.label || font.family,
}));
